import { useQuery } from "@tanstack/react-query"
import { marked } from "marked"
import PropTypes from "prop-types"
import { useCallback } from "react"
import { Helmet } from "react-helmet-async"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import api from "apis"
import GenericNotFound from "app/generic-not-found"
import FAQAccordion from "areas/landing/elements/faq/accordion"
import Divider from "components/ui/divider"
import Picture from "components/ui/picture"
import Shimmer from "components/ui/shimmer"

import ArticleSection from "./article-section"
import Breadcrumb from "./breadcrumb"
import ShareArticle from "./share-article"

const Article = ({ articleData, staticContext }) => {
  const { article_path } = useParams()
  const queryFn = useCallback(
    () => api.get(`/blog/${article_path}.json`),
    [article_path]
  )

  const ssrData = typeof window === "undefined" ? articleData : undefined
  const ssrNotFoundPath = useSelector(
    (state) => state.collection.ssr_not_found_path
  )
  const disabled =
    `${sl.basename || ""}/blog/${article_path}` === ssrNotFoundPath

  const { data, isError, isLoading } = useQuery({
    enabled: !disabled,
    keepPreviousData: true,
    queryFn,
    queryKey: ["blog", article_path],
    ...(ssrData && {
      initialData: ssrData,
    }),
  })

  if (isError || disabled) {
    return <GenericNotFound staticContext={staticContext} />
  }

  const { article } = data || {}

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable sort-keys, react/jsx-sort-props, react/no-unknown-property */}
        {(article?.seo_title || article?.title) && (
          <>
            <title>{article.seo_title || article.title}</title>
            <meta
              content={article.seo_title || article.title}
              property="og:title"
            />
          </>
        )}
        {article?.seo_description && (
          <meta content={article.seo_description} name="description" />
        )}
        {article?.seo_description && (
          <meta content={article.seo_description} property="og:description" />
        )}
        <meta content="article" property="og:type" />
        {article?.url_path && (
          <meta
            content={`https://${sl.config.site_url}/blog/${article.url_path}`}
            property="og:url"
          />
        )}
        {article?.cover_image?.fallback && (
          <meta
            content={article.cover_image.fallback}
            name="og_image"
            property="og:image"
          />
        )}
        {article?.cover_image?.srcSet?.length > 0 &&
          article.cover_image.srcSet.map((srcset, i) => (
            <link
              key={`article-cover-image-${article.cover_image.id || i}-${
                srcset.media
              }`}
              rel="preload"
              fetchpriority="high"
              as="image"
              imagesrcset={srcset.srcSet}
              imagesizes={srcset.sizes}
              media={srcset.media}
            />
          ))}
        {article?.updatedAt && (
          <meta content={article.updatedAt} property="article:modified_time" />
        )}
        {article && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `https://${sl.config.site_url}/blog/${article.url_path}`,
              },
              headline: article.title,
              ...(article.cover_image?.fallback && {
                image: article.cover_image.fallback,
              }),
              ...(article.updatedAt && {
                dateModified: article.updatedAt,
              }),
              author: {
                "@type": "Organization",
                name: sl.config.site_title,
                url: `https://${sl.config.site_url}`,
              },
              publisher: {
                "@type": "Organization",
                name: sl.config.site_title,
                url: `https://${sl.config.site_url}/blog/${article.url_path}`,
                logo: {
                  "@type": "ImageObject",
                  url: "https://assets.jewlr.com/images/logos/jewlr-logo.svg",
                },
              },
            })}
          </script>
        )}
        {article?.faqs?.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: article.faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.rich_answer
                    ? marked.parseInline(faq.rich_answer)
                    : faq.answer,
                },
              })),
            })}
          </script>
        )}
        {/* eslint-enable sort-keys, react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      <Breadcrumb article={article?.title} isLoading={isLoading} />
      {isLoading ? (
        <>
          <Shimmer
            className="tablet:mb-32 mb-24 w-full"
            style={{ height: "54px" }}
          />
          <Shimmer
            className="tablet:mb-48 pb-(--base-padding-bottom) tablet:pb-(--tablet-padding-bottom) mb-16 w-full"
            style={{
              "--base-padding-bottom": "65.89%",
              "--tablet-padding-bottom": "40.32%",
            }}
          />
          <Shimmer
            className="desktop:w-900 mx-auto w-full"
            style={{ height: "80vh" }}
          />
        </>
      ) : (
        <>
          <div className="tablet:mb-32 mb-24">
            <h1 className="text-30 font-bold leading-none">{article.title}</h1>
            {article.formattedUpdatedAt && (
              <p className="body-sm leading-16 text-grey-65 mt-8">
                Last Updated: {article.formattedUpdatedAt}
              </p>
            )}
          </div>
          {article.cover_image && (
            <div className="tablet:mb-48 mb-16">
              <Picture
                alt={article.cover_image.alt}
                fallback={article.cover_image.fallback}
                srcSet={article.cover_image.srcSet}
              />
            </div>
          )}
          <div className="desktop:w-900 mx-auto w-full">
            {article.sections &&
              article.sections.map((section, i) => (
                <ArticleSection key={i} section={section} />
              ))}
            <Divider className="mb-24 mt-60" />
            {(article.faqs || []).map((faq) => (
              <FAQAccordion
                elementId="blog"
                item={faq}
                key={`faq-accordion-${faq.id}`}
              />
            ))}
            <ShareArticle article={article} />
          </div>
        </>
      )}
    </>
  )
}

Article.propTypes = {
  articleData: PropTypes.object,
  staticContext: PropTypes.object,
}

export default Article
